import store from 'app/store';
import {
  AnySizingItemAttributeType,
  AnySizingItemType,
  AttomDataType,
  DoorItemType,
  SizingItemsType,
  ValidSizingCalculatorAttributesType
} from 'data/types';
// Deep imports (not the feature barrels): structure-thunks imports this
// module, so pulling the barrels here would create an import cycle.
import {
  hydrateDefaultSizingItems,
  setDefaultSizingItems
} from 'features/data/data-slice';
import { setIsNewlyCreatedStructure } from 'features/structure/structure-slice';
import axios from 'utils/api';
import { isEmpty, isNotEmpty } from 'utils/helpers';
import { getProjectSettingValue } from 'utils/project';
import { applyInsulationDefaultsToSizingItems } from 'utils/recommended-sizing-defaults';
import { sizingDefaultsCategories } from 'utils/sizing-item-configs';
import { SpaceDetailsCategory } from 'utils/structure-space-details-types';

export function isValidSizingItem(
  sizingItemType: keyof ValidSizingCalculatorAttributesType,
  sizingItem: AnySizingItemType
): boolean {
  const validCombinations =
    store.getState().data.validSizingAttributes[sizingItemType];

  return validCombinations?.some((combo) =>
    Object.keys(combo).every(
      (key) =>
        combo[key] === sizingItem[key] ||
        (isEmpty(combo[key]) && isEmpty(sizingItem[key]))
    )
  );
}

export function normalizeSolidDoorAttributes<T extends Partial<DoorItemType>>(
  door: T,
  validCombinations: Partial<DoorItemType>[]
): T & { valid: boolean } {
  if (!validCombinations || validCombinations.length === 0) {
    return { ...door, valid: true };
  }

  if (isValidSizingItem('solidDoors', door as AnySizingItemType)) {
    return { ...door, valid: true };
  }

  const matchingMaterial = validCombinations.find(
    (combo) => combo.material === door.material
  );
  const target = matchingMaterial ?? validCombinations[0];

  const normalized = {
    ...door,
    ...(target.material !== undefined && { material: target.material }),
    ...(target.stormMaterial !== undefined && {
      stormMaterial: target.stormMaterial
    }),
    ...(target.core !== undefined && { core: target.core })
  } as T;

  return {
    ...normalized,
    valid: isValidSizingItem('solidDoors', normalized as AnySizingItemType)
  };
}

export function areAllDefaultSizingItemsValid(
  defaultSizingItems: SizingItemsType
): boolean {
  const { validSizingAttributes } = store.getState().data;

  for (const category of sizingDefaultsCategories) {
    for (const itemConfig of category.items) {
      const currentValues = defaultSizingItems[
        itemConfig.defaultsKey as keyof SizingItemsType
      ] as Record<string, unknown>;

      if (!currentValues) {
        return false;
      }

      const validCombinations =
        validSizingAttributes[itemConfig.validAttributeKey];

      if (!validCombinations || validCombinations.length === 0) {
        // eslint-disable-next-line no-continue
        continue;
      }

      const mockItem = itemConfig.getMockItem(
        currentValues
      ) as AnySizingItemType;

      const mockItemKeys = Object.keys(mockItem);
      const hasValidCombination = validCombinations.some((combo) =>
        Object.keys(combo)
          .filter((key) => mockItemKeys.includes(key))
          .every(
            (key) =>
              combo[key] === mockItem[key] ||
              (isEmpty(combo[key]) && isEmpty(mockItem[key]))
          )
      );

      if (!hasValidCombination) {
        return false;
      }
    }
  }

  return true;
}

export function getValidSizingAttributeOptions(
  sizingItemType: keyof ValidSizingCalculatorAttributesType,
  sizingItem: AnySizingItemType,
  attribute: AnySizingItemAttributeType,
  labelFormat?: (value: any) => string
) {
  const validCombinations =
    store.getState().data.validSizingAttributes[sizingItemType];
  const allOptions: any[] = [];
  const validOptions: any[] = [];

  validCombinations.forEach((combo) => {
    if (isNotEmpty(combo[attribute])) {
      allOptions.push(combo[attribute]);
    }

    if (
      Object.keys(combo)
        .filter(
          (key) => key !== attribute && Object.keys(sizingItem).includes(key)
        )
        .every(
          (key) =>
            combo[key] === sizingItem[key] ||
            (isEmpty(combo[key]) && isEmpty(sizingItem[key])) ||
            isEmpty(sizingItem[key])
        )
    ) {
      validOptions.push(combo[attribute]);
    }
  });

  const uniqueOptions = Array.from(new Set(allOptions));

  const result = uniqueOptions.map((option) => {
    const formattedLabel = labelFormat ? labelFormat(option) : option;
    return {
      value: option,
      label: validOptions.includes(option)
        ? formattedLabel
        : `${formattedLabel} (Invalid)`
    };
  });
  return result;
}

export function calculateNewWindowDimensions(params: {
  wallWidth: number;
  wallHeight: number;
  numWindows: number;
  fraction?: number;
  aspectRatio?: number;
  fixedWidth?: number;
  fixedHeight?: number;
}) {
  const {
    wallWidth,
    wallHeight,
    numWindows,
    fraction = 0.2,
    aspectRatio,
    fixedWidth,
    fixedHeight
  } = params;

  if (numWindows < 1) {
    return { width: 0, height: 0 };
  }

  if (fraction <= 0 || fraction > 1) {
    return { width: 0, height: 0 };
  }

  if (fixedWidth !== undefined && fixedWidth <= 0) {
    return { width: 0, height: 0 };
  }

  if (fixedHeight !== undefined && fixedHeight <= 0) {
    return { width: 0, height: 0 };
  }

  const wallArea = wallWidth * wallHeight;
  const totalWinArea = wallArea * fraction;
  const areaPerWin = totalWinArea / numWindows;

  let w: number;
  let h: number;

  if (fixedHeight !== undefined) {
    h = fixedHeight;
    w = areaPerWin / h;
  } else if (fixedWidth !== undefined) {
    w = fixedWidth;
    h = areaPerWin / w;
  } else if (aspectRatio !== undefined && aspectRatio > 0) {
    h = Math.sqrt(areaPerWin / aspectRatio);
    w = h * aspectRatio;
  } else {
    w = Math.sqrt(areaPerWin);
    h = w;
  }

  return { width: Math.ceil(w), height: Math.ceil(h) };
}

export function getAccuracyMessage(accuracy: number): string {
  if (accuracy >= 90) return 'Your estimate includes detailed measurements';
  if (accuracy >= 80) return 'Add more details below to improve accuracy';
  return 'Complete the steps below for a better sizing estimate';
}

export function getAccuracyLabel(accuracy: number): string {
  if (accuracy >= 90) return 'Pro-level accuracy';
  if (accuracy >= 80) return 'Good for estimates';
  return 'Keep adding details';
}

export function getAccuracyLevel(value: number) {
  if (value >= 90) return { label: 'Excellent', color: 'text-emerald-600' };
  if (value >= 80) return { label: 'Good', color: 'text-green-600' };
  if (value >= 70) return { label: 'Fair', color: 'text-amber-600' };
  return { label: 'Basic', color: 'text-orange-600' };
}

export async function updateDefaultSizingItemInsulationValues(
  attomData: AttomDataType,
  climateZone: string
): Promise<SizingItemsType> {
  const { defaultSizingItems } = store.getState().data;
  const yearBuilt =
    getProjectSettingValue('year_built') || attomData.summary.yearBuilt;
  const insulationDefaults = await axios.get(
    `sizing/insulation-defaults?year=${yearBuilt}&climateZone=${climateZone}`
  );

  if (insulationDefaults.data) {
    return applyInsulationDefaultsToSizingItems(
      defaultSizingItems,
      insulationDefaults.data
    );
  }

  return defaultSizingItems;
}

// Seeds the insulation defaults from the fresh year-built + climate-zone
// recommendation (GET sizing/insulation-defaults) and decides whether to
// PERSIST them. For a newly created structure (isNewlyCreatedStructure flag)
// the seed is dispatched via setDefaultSizingItems, which bumps
// defaultSizingItemsVersion — that bump both makes the autosave watcher
// persist structure-specific default rows AND arms fetchSizingItemDefaults'
// stale-response guard, so an in-flight fetch can no longer hydrate the
// seed back to the system-wide globals. Existing structures keep the old
// hydrate-only behavior (no version bump, no save).
//
// Edge cases: without a year built (no project setting and no ATTOM year)
// the endpoint cannot be called, so the structure keeps whatever the slice
// holds (the global defaults) until an address lookup provides a year. A
// failed request is swallowed the same way, and the new-structure flag is
// only cleared on success so a later ATTOM re-trigger can retry the seed.
export async function seedDefaultSizingItemInsulationValues(
  attomData: AttomDataType,
  climateZone: string
): Promise<void> {
  const yearBuilt =
    getProjectSettingValue('year_built') || attomData.summary.yearBuilt;

  if (isEmpty(yearBuilt)) {
    return;
  }

  let insulationDefaults: SizingItemsType;

  try {
    insulationDefaults = await updateDefaultSizingItemInsulationValues(
      attomData,
      climateZone
    );
  } catch (error) {
    console.error('Error fetching insulation defaults', error);
    return;
  }

  if (store.getState().structure.isNewlyCreatedStructure) {
    store.dispatch(setDefaultSizingItems(insulationDefaults));
    store.dispatch(setIsNewlyCreatedStructure(false));
  } else {
    store.dispatch(hydrateDefaultSizingItems(insulationDefaults));
  }
}

export type SizingItemsOrderType = 'default' | 'windows-doors' | 'appliances';

export function getSizingOrder(
  sectionId?: string,
  spaceDetailsCategory?: SpaceDetailsCategory
): SizingItemsOrderType {
  if (sectionId !== 'space_details') {
    return 'default';
  }

  if (spaceDetailsCategory === 'windows') {
    return 'windows-doors';
  }

  if (spaceDetailsCategory === 'appliances') {
    return 'appliances';
  }

  return 'default';
}
